import InsertDriveFileOutlined from "@mui/icons-material/InsertDriveFileOutlined";
import PaletteOutlined from "@mui/icons-material/PaletteOutlined";
import PhotoLibraryOutlined from "@mui/icons-material/PhotoLibraryOutlined";
import PictureAsPdfOutlined from "@mui/icons-material/PictureAsPdfOutlined";
import VideocamOutlined from "@mui/icons-material/VideocamOutlined";
import { Box, Link } from "@mui/material";
import { Fragment } from "react";
import { Link as RouterLink } from "react-router";
import { assetPreviewPath } from "../../../../utils/asset-link-url";
import { useArchiveDesign } from "../../../assets/hooks/use-archive-design";
import { DuplicateAssetButton } from "./duplicate-asset-button";
import { RecentlyEditedMenu } from "./recently-edited-menu";
import { RecentlyEditedRow, RowDetail } from "./recently-edited-row";

import type { FeedItem } from "../../hooks/use-recently-edited";
import type { FC } from "react";

const iconSx = { fontSize: 40, color: "text.secondary" };

const AssetMedia: FC<{ asset: FeedItem }> = ({ asset }) => {
  if (asset.thumbnail_url)
    return (
      <Box
        component="img"
        src={asset.thumbnail_url}
        alt=""
        sx={{ width: "100%", height: "100%", objectFit: "cover" }}
      />
    );
  if (asset.mime_type === "application/pdf")
    return <PictureAsPdfOutlined data-testid="pdf-icon" sx={iconSx} />;
  if (asset.content_type === "Video" || asset.mime_type?.startsWith("video/"))
    return <VideocamOutlined data-testid="video-icon" sx={iconSx} />;
  return <InsertDriveFileOutlined data-testid="file-icon" sx={iconSx} />;
};

/**
 * A Digital Asset in the feed: its type, and the folders it's in as links into them. It opens in
 * the Content Library's preview, inside its folder, as uploaded files have no Design Huddle project
 * to edit.
 */
export const AssetRow: FC<{
  asset: FeedItem;
  /** Shows a copy made with its duplicate button. */
  onDuplicated: (copy: FeedItem) => void;
  /** Takes it out of the feed once it's archived. */
  onArchived: () => void;
}> = ({ asset, onDuplicated, onArchived }) => {
  const { archive, archiving } = useArchiveDesign(asset.id);

  return (
    <RecentlyEditedRow
      media={<AssetMedia asset={asset} />}
      title={asset.title}
      // Inside the folder it's in, the last of its path.
      href={assetPreviewPath(asset.id, asset.folder_path?.at(-1)?.id)}
      analyticsId="dashboard-recently-edited-asset"
      action={
        <>
          <DuplicateAssetButton asset={asset} onDuplicated={onDuplicated} />
          <RecentlyEditedMenu
            title={asset.title}
            label="Design"
            archive={archive}
            archiving={archiving}
            onArchived={onArchived}
          />
        </>
      }
      details={
        <>
          {asset.content_type && (
            <RowDetail icon={<PaletteOutlined />}>{asset.content_type}</RowDetail>
          )}
          <RowDetail icon={<PhotoLibraryOutlined color="primary" />}>
            <span>
              <Link
                component={RouterLink}
                to="/content-library"
                underline="hover"
                data-analytics-id="dashboard-recently-edited-asset-library"
              >
                Content Library
              </Link>
              {asset.folder_path?.map(({ id, name }) => (
                <Fragment key={id}>
                  <Box component="span" sx={{ color: "primary.main" }}>
                    {" / "}
                  </Box>
                  <Link
                    component={RouterLink}
                    to={`/content-library?folder=${id}`}
                    underline="hover"
                    data-analytics-id="dashboard-recently-edited-asset-folder"
                  >
                    {name}
                  </Link>
                </Fragment>
              ))}
            </span>
          </RowDetail>
        </>
      }
    />
  );
};
